import { BarChartIcon, Cross2Icon, DesktopIcon, GearIcon, HamburgerMenuIcon, IdCardIcon, RocketIcon } from "@radix-ui/react-icons";
import { Avatar, Box, Button, Flex, Heading, Progress, Text } from "@radix-ui/themes";
import { type ReactNode, useState } from "react";
import { NavLink } from "react-router-dom";
import logo from "../../assets/logo.png";
import { useAuth } from "../../contexts/AuthContext";
import { useUsage } from "../../contexts/UsageContext";
import { useServiceNotice } from "../../hooks/useServiceNotice";
import { emailInitials } from "../../utils/ui";
import { formatResetDate } from "../../utils/usageMonths";
import Footer from "../shared/Footer";
import ServiceNoticeBanner from "./ServiceNoticeBanner";
import { MONO_STYLE, PANEL_STYLE } from "./tokens";
import UpgradeForm from "./UpgradeForm";
import UsageLimitBanner from "./UsageLimitBanner";

const NAV_ITEMS = [
    {
        to: "/",
        label: "Devices",
        icon: DesktopIcon,
        end: true,
    },
    {
        to: "/usage",
        label: "Usage",
        icon: BarChartIcon,
        end: false,
    },
    {
        to: "/billing",
        label: "Plan & billing",
        icon: IdCardIcon,
        end: false,
    },
    {
        to: "/settings",
        label: "Settings",
        icon: GearIcon,
        end: false,
    },
];

const SIDEBAR_WIDTH = 272;

/** Sidebar mini widget: real usage from the same store `who_am_i` reads from. */
function UsageMiniWidget() {
    const { usage, loading, billingVisible } = useUsage();

    if (loading || !usage) {
        return null;
    }

    const isPro = usage.plan === "pro";

    // A free viewer gets nothing here while the gate is off/shadow — showing a
    // usage bar with no upgrade path (or an upgrade path to nothing enforced)
    // is just confusing. An existing Pro subscriber always sees their own
    // status regardless of gate mode (see billingVisible in UsageContext).
    if (!isPro && !billingVisible) {
        return null;
    }

    return (
        <Box m="3" p="3" style={PANEL_STYLE}>
            <Flex justify="between" align="center" mb="2">
                <Text size="1" color="gray">
                    Tool calls
                </Text>
                {isPro ? (
                    <Text size="1" weight="bold" color="blue">
                        Pro
                    </Text>
                ) : (
                    <Text size="1" color="gray">
                        {Math.min(100, Math.round(usage.pct ?? 0))}%
                    </Text>
                )}
            </Flex>
            {!isPro && <Progress value={Math.min(100, usage.pct ?? 0)} size="1" mb="2" />}
            <Text as="p" size="1" color="gray" mb="2" style={MONO_STYLE}>
                {usage.callsUsed.toLocaleString()}{" "}
                {usage.callsIncluded !== null ? `of ${usage.callsIncluded.toLocaleString()} this month` : "this month · no limit"}
            </Text>
            {usage.callsIncluded !== null && (
                <Text as="p" size="1" color="gray" mt="-1" mb="2">
                    Resets on {formatResetDate(usage.resetsAt)}
                </Text>
            )}
            {!isPro && (
                <UpgradeForm
                    surface="sidebar_card"
                    size="2"
                    style={{
                        width: "100%",
                    }}
                >
                    <RocketIcon aria-hidden="true" />
                </UpgradeForm>
            )}
        </Box>
    );
}

interface DashboardShellProps {
    title: string;
    subtitle?: string;
    action?: ReactNode;
    children: ReactNode;
}

const DashboardShell = ({ title, subtitle, action, children }: DashboardShellProps) => {
    const { user } = useAuth();
    const { billingVisible } = useUsage();
    const serviceNotice = useServiceNotice();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    // Same rule as the sidebar widget: no "Plan & billing" nav entry while the
    // gate is off/shadow, unless this user already has Pro to manage.
    const navItems = NAV_ITEMS.filter((item) => item.to !== "/billing" || billingVisible);

    return (
        // Fixed to the viewport edges: the app shell's `body { display: flex;
        // justify-content: center }` (for centering the sign-in/onboarding cards
        // elsewhere) shrink-wraps its child to content width, so a plain
        // `width: 100%` here would resolve against that shrunk box instead of
        // the viewport. Taking this out of flow with `position: fixed` sidesteps
        // it entirely.
        // backgroundColor here too, deliberately — this shell shouldn't depend on
        // body's own background being correct underneath it. index.css already
        // fixes that (see the comment there), but a fixed-position full-viewport
        // app shell owning its own background is the more robust default
        // regardless: nothing here relies on what happens to be painted behind it.
        //
        // The banner is a sibling ABOVE dc-dashboard-shell, not a child inside
        // it, specifically so it spans the full viewport width above the
        // sidebar too (not just the content column) and never scrolls out of
        // view: this outer Flex is a fixed-height column, so the banner
        // permanently occupies its own row and dc-dashboard-shell (flex: 1)
        // simply gets the remaining height — no position:sticky needed here
        // (ServiceNoticeBanner still sets it for the case it's reused inside a
        // scroll container elsewhere).
        <Flex
            direction="column"
            style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "var(--dc-bg)",
            }}
        >
            {serviceNotice && <ServiceNoticeBanner notice={serviceNotice} />}
            <Flex
                className="dc-dashboard-shell"
                style={{
                    flex: 1,
                    minHeight: 0,
                }}
            >
                <Flex className="dc-mobile-topbar" align="center" justify="between">
                    <Flex
                        align="center"
                        gap="2"
                        style={{
                            minWidth: 0,
                        }}
                    >
                        <img
                            src={logo}
                            alt=""
                            style={{
                                width: 28,
                                height: 28,
                                objectFit: "contain",
                                flex: "0 0 auto",
                            }}
                        />
                        <Box
                            style={{
                                minWidth: 0,
                            }}
                        >
                            <Text as="div" size="2" weight="bold" truncate>
                                Desktop Commander
                            </Text>
                            <Text as="div" size="1" color="gray">
                                Remote MCP
                            </Text>
                        </Box>
                    </Flex>
                    <Button type="button" variant="ghost" color="gray" aria-label="Open navigation" onClick={() => setMobileNavOpen(true)}>
                        <HamburgerMenuIcon width="20" height="20" />
                    </Button>
                </Flex>

                {mobileNavOpen && (
                    <button type="button" className="dc-dashboard-backdrop" aria-label="Close navigation" onClick={() => setMobileNavOpen(false)} />
                )}
                {/* Sidebar */}
                <Flex
                    direction="column"
                    justify="between"
                    className={`dc-dashboard-sidebar${mobileNavOpen ? " is-open" : ""}`}
                    style={{
                        width: SIDEBAR_WIDTH,
                        flex: `0 0 ${SIDEBAR_WIDTH}px`,
                        backgroundColor: "var(--dc-bg)",
                        borderRight: "1px solid var(--dc-card-border)",
                        height: "100%",
                        overflowY: "auto",
                    }}
                >
                    <Box>
                        <Flex align="center" gap="2" p="4">
                            <img
                                src={logo}
                                alt=""
                                style={{
                                    width: 28,
                                    height: 28,
                                    objectFit: "contain",
                                }}
                            />
                            <Box
                                style={{
                                    minWidth: 0,
                                }}
                            >
                                <Text as="div" size="2" weight="bold">
                                    Desktop Commander
                                </Text>
                                <Text as="div" size="1" color="gray">
                                    Remote MCP
                                </Text>
                            </Box>
                            <Button
                                className="dc-mobile-nav-close"
                                type="button"
                                variant="ghost"
                                color="gray"
                                aria-label="Close navigation"
                                onClick={() => setMobileNavOpen(false)}
                                style={{
                                    marginLeft: "auto",
                                }}
                            >
                                <Cross2Icon width="18" height="18" />
                            </Button>
                        </Flex>

                        <Flex direction="column" gap="1" px="2">
                            {navItems.map(({ to, label, icon: Icon, end }) => (
                                <NavLink
                                    key={to}
                                    to={to}
                                    end={end}
                                    onClick={() => setMobileNavOpen(false)}
                                    style={({ isActive }) => ({
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        padding: "8px 12px",
                                        fontSize: "13.5px",
                                        fontWeight: 500,
                                        textDecoration: "none",
                                        color: isActive ? "var(--blue-9)" : "var(--dc-muted)",
                                    })}
                                >
                                    <Icon aria-hidden="true" />
                                    {label}
                                </NavLink>
                            ))}
                        </Flex>
                    </Box>

                    <Box>
                        <UsageMiniWidget />
                        <NavLink
                            to="/settings"
                            style={{
                                textDecoration: "none",
                                color: "inherit",
                            }}
                            aria-label="Account settings"
                        >
                            <Flex
                                align="center"
                                gap="2"
                                p="3"
                                style={{
                                    borderTop: "1px solid var(--dc-card-border)",
                                }}
                            >
                                <Avatar size="1" radius="full" fallback={emailInitials(user?.email)} />
                                <Text size="1" color="gray" truncate>
                                    {user?.email}
                                </Text>
                            </Flex>
                        </NavLink>
                        <Footer border={false} />
                    </Box>
                </Flex>

                {/* Main content */}
                <Box
                    className="dc-dashboard-main"
                    style={{
                        flex: 1,
                        height: "100%",
                        overflowY: "auto",
                        minWidth: 0,
                    }}
                >
                    <Box
                        className="dc-dashboard-content"
                        style={{
                            maxWidth: 880,
                            margin: "0 auto",
                            padding: "48px 32px 64px",
                            boxSizing: "border-box",
                        }}
                    >
                        <UsageLimitBanner />
                        <Flex justify="between" align="start" mb="5" gap="4" wrap="wrap">
                            <Box>
                                <Heading size="7" mb="1">
                                    {title}
                                </Heading>
                                {subtitle && (
                                    <Text as="p" size="2" color="gray">
                                        {subtitle}
                                    </Text>
                                )}
                            </Box>
                            {action}
                        </Flex>
                        {children}
                    </Box>
                </Box>
            </Flex>
        </Flex>
    );
};

export default DashboardShell;
